import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router';
import { fetchUpcomingTournaments } from '../../services/tournamentCalendarService';
import {
    DuplicateRegistrationError,
    fetchRegistrations,
    registerOD,
    registerOE,
} from '../../services/registrationService';
import { buildFlyerUrl } from '../../services/flyerService';
import { logError } from '../../utils/errorHandlers';
import { formatRelativeTime } from '../../utils/dateUtils';
import { Avatar } from '../common/Avatar';
import SuccessCard from './SuccessCard';

const formatDate = (isoString) => {
    if (!isoString) {
        return 'Unbekanntes Datum';
    }

    const parsed = new Date(isoString);
    if (Number.isNaN(parsed.getTime())) {
        return isoString;
    }

    return parsed.toLocaleString('de-DE', {
        weekday: 'short',
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
    });
};

const getInitialFormValues = () => ({
    playerOne: '',
    playerTwo: '',
});

const buildSuccessMessage = (tournament) => {
    if (!tournament) {
        return 'Anmeldung gespeichert.';
    }

    const readableDate = formatDate(tournament.startDateTime || tournament.date);
    return `Du bist für ${readableDate} – ${tournament.title} angemeldet.`;
};

const RegistrationSkeleton = () => {
    const location = useLocation();
    const preselectedTournamentId = useMemo(() => {
        const params = new URLSearchParams(location.search || '');
        return params.get('tournamentId') || '';
    }, [location.search]);
    const [status, setStatus] = useState('idle'); // idle | loading | ready | empty | error
    const [tournaments, setTournaments] = useState([]);
    const [selectedTournamentId, setSelectedTournamentId] = useState('');
    const [formValues, setFormValues] = useState(getInitialFormValues);
    const [fieldTouched, setFieldTouched] = useState({ playerOne: false, playerTwo: false });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submissionFeedback, setSubmissionFeedback] = useState(null);
    const [registrationsByTournament, setRegistrationsByTournament] = useState({});
    const [registrationsLoading, setRegistrationsLoading] = useState(false);
    const [registrationsError, setRegistrationsError] = useState(null);
    const [reloadRegistrationsToken, setReloadRegistrationsToken] = useState(0);

    useEffect(() => {
        let isMounted = true;
        setStatus('loading');

        fetchUpcomingTournaments()
            .then((result = {}) => {
                if (!isMounted) {
                    return;
                }

                const odEntries = Array.isArray(result.od) ? result.od : [];
                const oeEntries = Array.isArray(result.oe) ? result.oe : [];
                const merged = [...odEntries, ...oeEntries].sort((a, b) => {
                    const aTime = new Date(a.startDateTime).getTime();
                    const bTime = new Date(b.startDateTime).getTime();
                    return aTime - bTime;
                });

                setTournaments(merged);
                if (merged.length > 0) {
                    const match = preselectedTournamentId
                        ? merged.find((entry) => entry.tournamentId === preselectedTournamentId)
                        : null;
                    const initialSelection = (match || merged[0]).tournamentId;
                    setSelectedTournamentId(initialSelection);
                    setStatus('ready');
                } else {
                    setSelectedTournamentId('');
                    setStatus('empty');
                }
            })
            .catch((error) => {
                if (!isMounted) {
                    return;
                }
                logError('[registration-ui-skeleton] Failed to load upcoming tournaments', error);
                setStatus('error');
            });

        return () => {
            isMounted = false;
        };
    }, [preselectedTournamentId]);

    const selectedTournament = useMemo(
        () => tournaments.find((entry) => entry.tournamentId === selectedTournamentId),
        [tournaments, selectedTournamentId]
    );

    const selectedTournamentFlyerUrl = useMemo(() => {
        if (!selectedTournament) {
            return null;
        }
        const tournamentId = selectedTournament.tournamentId || selectedTournament.id;
        return buildFlyerUrl({ tournamentId });
    }, [selectedTournament]);

    useEffect(() => {
        setFormValues(getInitialFormValues());
        setFieldTouched({ playerOne: false, playerTwo: false });
        setSubmissionFeedback(null);
    }, [selectedTournamentId]);

    useEffect(() => {
        if (!selectedTournamentId) {
            return undefined;
        }

        let isMounted = true;
        setRegistrationsError(null);
        setRegistrationsLoading(true);

        fetchRegistrations(selectedTournamentId)
            .then((entries = []) => {
                if (!isMounted) {
                    return;
                }

                setRegistrationsByTournament((prev) => ({
                    ...prev,
                    [selectedTournamentId]: entries,
                }));
            })
            .catch((error) => {
                if (!isMounted) {
                    return;
                }
                logError(
                    `[registration-ui-skeleton] Failed to load registrations for ${selectedTournamentId}`,
                    error
                );
                setRegistrationsError('Die aktuelle Anmeldeliste konnte nicht geladen werden.');
            })
            .finally(() => {
                if (isMounted) {
                    setRegistrationsLoading(false);
                }
            });

        return () => {
            isMounted = false;
        };
    }, [selectedTournamentId, reloadRegistrationsToken]);

    const getNameError = (name) => {
        const isRequired = selectedTournament?.type === 'OD' || name === 'playerOne';
        if (!isRequired) {
            return null;
        }
        const raw = formValues[name] ?? '';
        const value = raw.trim();
        if (!value) {
            return 'Bitte Namen eintragen.';
        }
        if (value.length < 2) {
            return 'Name muss mindestens 2 Zeichen lang sein.';
        }
        return null;
    };

    const isFieldValid = (name) => {
        const error = getNameError(name);
        return error === null;
    };

    const isFormValid = Boolean(
        selectedTournament && isFieldValid('playerOne') && (selectedTournament.type !== 'OD' || isFieldValid('playerTwo'))
    );

    const handleTournamentChange = (event) => {
        setSelectedTournamentId(event.target.value);
    };

    const handleInputChange = (event) => {
        const { name, value } = event.target;
        setFormValues((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleBlur = (event) => {
        const { name } = event.target;
        setFieldTouched((prev) => ({ ...prev, [name]: true }));
    };

    const triggerRegistrationsReload = () => {
        setReloadRegistrationsToken((prev) => prev + 1);
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setFieldTouched({ playerOne: true, playerTwo: true });
        setSubmissionFeedback(null);

        if (!isFormValid || !selectedTournament) {
            return;
        }

        const payload = {
            tournamentId: selectedTournament.tournamentId,
            tournamentDate: selectedTournament.date,
            player1Name: formValues.playerOne.trim(),
        };

        if (selectedTournament.type === 'OD') {
            payload.player2Name = formValues.playerTwo.trim();
        }

        setIsSubmitting(true);

        try {
            if (selectedTournament.type === 'OD') {
                await registerOD(payload);
            } else {
                await registerOE(payload);
            }

            setSubmissionFeedback({
                type: 'success',
                message: buildSuccessMessage(selectedTournament),
            });
            triggerRegistrationsReload();
        } catch (error) {
            if (error instanceof DuplicateRegistrationError) {
                setSubmissionFeedback({
                    type: 'duplicate',
                    message:
                        selectedTournament.type === 'OD'
                            ? 'Dieses Team ist für dieses Turnier bereits registriert.'
                            : 'Du bist für dieses Turnier bereits registriert.',
                });
            } else {
                logError('[registration-ui-skeleton] Anmeldung fehlgeschlagen', error);
                setSubmissionFeedback({
                    type: 'error',
                    message: 'Anmeldung ist gerade nicht möglich. Bitte versuch es in wenigen Minuten erneut.',
                });
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const currentRegistrations = registrationsByTournament[selectedTournamentId] || [];

    const renderRegistrations = () => {
        if (registrationsLoading) {
            return (
                <p className="registration-status registration-status--info">
                    <span className="registration-spinner" aria-hidden="true" /> Anmeldungen werden geladen…
                </p>
            );
        }

        if (registrationsError) {
            return (
                <p className="registration-status registration-status--error">{registrationsError}</p>
            );
        }

        if (currentRegistrations.length === 0) {
            return <p className="registration-status">Noch keine Anmeldungen für dieses Turnier.</p>;
        }

        return (
            <ul className="registration-list">
                {currentRegistrations.map((entry, index) => {
                    const displayName = entry.player2Name
                        ? `${entry.player1Name} & ${entry.player2Name}`
                        : entry.player1Name;
                    const key = entry.id || entry.registrationId || `${entry.player1Name}-${index}`;
                    const relativeTime = entry.createdAt ? formatRelativeTime(entry.createdAt) : null;

                    return (
                        <li key={key} className="registration-list__item">
                            <span className="registration-list__identity">
                                <Avatar name={entry.player1Name} size={36} data-testid="registration-avatar" />
                                <span className="registration-list__name">{displayName}</span>
                            </span>
                            {relativeTime && (
                                <span className="registration-list__meta" data-testid="registration-relative-time">{relativeTime}</span>
                            )}
                        </li>
                    );
                })}
            </ul>
        );
    };

    const renderContent = () => {
        if (status === 'loading' || status === 'idle') {
            return (
                <p className="registration-status registration-status--info">
                    <span className="registration-spinner" aria-hidden="true" /> Turnierdaten werden geladen…
                </p>
            );
        }

        if (status === 'error') {
            return (
                <p className="registration-status registration-status--error">
                    Kalenderfeed ist gerade nicht erreichbar. Bitte lade die Seite später erneut.
                </p>
            );
        }

        if (status === 'empty') {
            return (
                <p className="registration-status">
                    Aktuell sind keine kommenden OE/OD-Turniere im Kalender eingetragen.
                </p>
            );
        }

        return (
            <>
                <div className="registration-options" role="radiogroup" aria-label="Turnierauswahl">
                    {tournaments.map((tournament) => {
                        const tournamentId = tournament.tournamentId || tournament.id;
                        const flyerUrl = buildFlyerUrl({ tournamentId });

                        return (
                            <div key={tournamentId} className="tournament-option">
                                <div className="tournament-option-details">
                                    <label className="registration-option">
                                        <input
                                            type="radio"
                                            name="tournament"
                                            value={tournament.tournamentId}
                                            checked={selectedTournamentId === tournament.tournamentId}
                                            onChange={handleTournamentChange}
                                        />
                                        <div className="registration-option__content">
                                            <span className="registration-option__kind">{tournament.type}</span>
                                            <div className="registration-option__details">
                                                <span className="registration-option__title">{tournament.title}</span>
                                                <span className="registration-option__meta">
                                                    {formatDate(tournament.startDateTime)}
                                                </span>
                                            </div>
                                        </div>
                                    </label>
                                </div>
                                {flyerUrl && (
                                    <div className="tournament-option-flyer tournament-option-flyer-desktop">
                                        <img
                                            src={flyerUrl}
                                            alt={`Flyer ${tournament.title || 'Mini-Challenger'}`}
                                            loading="lazy"
                                        />
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>

                {selectedTournamentFlyerUrl && (
                    <section className="registration-flyer-mobile">
                        <h3>Flyer zum Teilen</h3>
                        <img
                            src={selectedTournamentFlyerUrl}
                            alt={`Flyer ${selectedTournament?.title || 'Mini-Challenger'}`}
                            loading="lazy"
                            data-testid="mobile-flyer-image"
                        />
                    </section>
                )}

                {selectedTournament ? (
                    <>
                        <p className="registration-hint">
                            Für OE genügt ein Name, bei OD benötigen wir beide Teammitglieder. Wir gleichen jede Angabe mit
                            der Live-Liste ab und verhindern so doppelte Einträge.
                        </p>

                        <form className="registration-form" onSubmit={handleSubmit} aria-busy={isSubmitting} data-testid="registration-form">
                            <div className="registration-fields">
                                <label className={`registration-field${fieldTouched.playerOne ? (isFieldValid('playerOne') ? ' registration-field--valid' : ' registration-field--invalid') : ''}`}>
                                    <span>Spieler:in*</span>
                                    <div className="registration-field__input-wrapper">
                                        <input
                                            type="text"
                                            name="playerOne"
                                            value={formValues.playerOne}
                                            onChange={handleInputChange}
                                            onBlur={handleBlur}
                                            placeholder="Name Spieler:in"
                                            aria-invalid={fieldTouched.playerOne && !isFieldValid('playerOne')}
                                            aria-describedby={fieldTouched.playerOne && !isFieldValid('playerOne') ? 'registration-error-player1' : undefined}
                                            data-testid="registration-player1"
                                        />
                                        {fieldTouched.playerOne && isFieldValid('playerOne') && (
                                            <span className="registration-field__check" aria-hidden="true" data-testid="registration-check-player1">&#10003;</span>
                                        )}
                                    </div>
                                    {fieldTouched.playerOne && !isFieldValid('playerOne') && (
                                        <span className="registration-field__error" id="registration-error-player1" data-testid="registration-error-player1">{getNameError('playerOne')}</span>
                                    )}
                                </label>

                                {selectedTournament.type === 'OD' && (
                                    <label className={`registration-field${fieldTouched.playerTwo ? (isFieldValid('playerTwo') ? ' registration-field--valid' : ' registration-field--invalid') : ''}`}>
                                        <span>Spieler:in 2*</span>
                                        <div className="registration-field__input-wrapper">
                                            <input
                                                type="text"
                                                name="playerTwo"
                                                value={formValues.playerTwo}
                                                onChange={handleInputChange}
                                                onBlur={handleBlur}
                                                placeholder="Name Spieler:in 2"
                                                aria-invalid={fieldTouched.playerTwo && !isFieldValid('playerTwo')}
                                                aria-describedby={fieldTouched.playerTwo && !isFieldValid('playerTwo') ? 'registration-error-player2' : undefined}
                                                data-testid="registration-player2"
                                            />
                                            {fieldTouched.playerTwo && isFieldValid('playerTwo') && (
                                                <span className="registration-field__check" aria-hidden="true" data-testid="registration-check-player2">&#10003;</span>
                                            )}
                                        </div>
                                        {fieldTouched.playerTwo && !isFieldValid('playerTwo') && (
                                            <span className="registration-field__error" id="registration-error-player2" data-testid="registration-error-player2">{getNameError('playerTwo')}</span>
                                        )}
                                    </label>
                                )}
                            </div>

                            <div className="registration-actions">
                                <button
                                    type="submit"
                                    className={`btn btn-custom${isSubmitting ? ' btn--submitting' : ''}`}
                                    disabled={!isFormValid || isSubmitting}
                                    data-testid="registration-submit"
                                >
                                    {isSubmitting ? (
                                        <><span className="registration-spinner" aria-hidden="true" data-testid="registration-submit-spinner" /> Wird gesendet…</>
                                    ) : (
                                        'Anmeldung absenden'
                                    )}
                                </button>
                                <p className="registration-helper">* Pflichtfelder · Wir bestätigen dir die Anmeldung sofort.</p>
                            </div>
                        </form>

                        <div className="registration-feedback-wrapper" aria-live="polite">
                             {submissionFeedback && submissionFeedback.type === 'success' && (
                                 <SuccessCard
                                     tournament={selectedTournament}
                                     onDismiss={() => setSubmissionFeedback(null)}
                                 />
                             )}
                             {submissionFeedback && submissionFeedback.type !== 'success' && (
                                 <p
                                   className={`registration-feedback registration-feedback--${submissionFeedback.type}`}
                                   data-testid={submissionFeedback.type === 'duplicate' ? 'registration-duplicate-warning' : 'registration-error'}
                                 >
                                     {submissionFeedback.message}
                                 </p>
                             )}
                        </div>

                        <div className="registration-list-wrapper">
                            <div className="registration-list-header">
                                <h3>Aktuelle Anmeldeliste</h3>
                            </div>
                            {renderRegistrations()}
                        </div>

                    </>
                ) : (
                    <p className="registration-status registration-status--info">Bitte Turnier auswählen.</p>
                )}
            </>
        );
    };

    return (
        <section className="registration-skeleton" aria-live="polite">
            <h2>Mini-Challenger-Anmeldung</h2>
            <p className="registration-note">
                Diese Anmeldung richtet sich an alle offenen Einzel (OE) und offenen Doppel (OD) der Mini-Challenger-Reihe. Wähle deinen Termin, ergänze die Namen und wir übernehmen euch ohne Umwege in die aktuelle Liste.
            </p>
            {renderContent()}
        </section>
    );
};

export default RegistrationSkeleton;
